// Inksake social menu v3. No dependencies and no network calls at page render.
// Only when a card or sheet opens: post images load from data-src, and the Facebook card gets the official
// Facebook Page plugin iframe (facebook.com/plugins/page.php). Nothing third-party loads before that.
const reduce = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
const fine = () => matchMedia('(hover: hover) and (pointer: fine)').matches;
const fbPlugin = (slot) => {
  const w = Math.max(180, Math.min(500, Math.round(slot.clientWidth || 320)));
  const q = new URLSearchParams({ href: slot.dataset.ikFbp, tabs: '', width: w, height: 70, small_header: 'true',
    hide_cover: 'true', show_facepile: 'false', adapt_container_width: 'true', hide_cta: 'true', lazy: 'true' });
  const f = document.createElement('iframe');
  f.src = `https://www.facebook.com/plugins/page.php?${q}`;
  f.width = w; f.height = 70; f.title = 'Inksake on Facebook (Follow button by Facebook)';
  f.loading = 'lazy'; f.allow = 'encrypted-media'; f.setAttribute('scrolling', 'no');
  f.addEventListener('load', () => slot.classList.add('is-loaded'), { once: true });
  slot.append(f); slot.removeAttribute('data-ik-fbp');
};
const hydrate = (scope) => {
  scope.querySelectorAll('img[data-src]').forEach((img) => {
    const done = () => img.closest('a')?.classList.add('is-loaded');
    img.addEventListener('load', done, { once: true });
    img.addEventListener('error', () => img.closest('li')?.remove(), { once: true });
    img.src = img.dataset.src; img.removeAttribute('data-src');
    if (img.complete && img.naturalWidth) done();
  });
  scope.querySelectorAll('[data-ik-fbp]').forEach(fbPlugin);
};
// X follow intent: Twitter's documented web intent opens in a 550x420 window on desktop (it cannot load in an iframe).
// On phones the plain link is kept so iOS/Android hand x.com links to the X app when it is installed.
document.addEventListener('click', (e) => {
  const a = e.target.closest?.('a.ik-btn[data-brand="x"]');
  if (!a || !fine() || e.metaKey || e.ctrlKey || e.shiftKey) return;
  const w = window.open(a.href, 'ik-x-intent', 'width=550,height=420');
  if (w) { w.opener = null; e.preventDefault(); } // popup blocked: the normal new-tab link still runs
});

class InksakeSocial extends HTMLElement {
  connectedCallback() {
    if (this.ready) return; this.ready = true;
    if (this.dataset.ctx === 'drawer') return this.#drawer();
    this.#bar();
  }

  /* ---- desktop bar: hover, focus and tap cards ---- */
  #bar() {
    const fine = matchMedia('(hover: hover) and (pointer: fine)').matches;
    this.timer = 0;
    this.querySelectorAll('.ik-social__item').forEach((item) => {
      const icon = item.querySelector('.ik-social__icon');
      if (fine) {
        item.addEventListener('mouseenter', () => this.#later(() => this.open(item), this.current ? 0 : 80));
        item.addEventListener('mouseleave', () => this.#later(() => this.close(), 260));
      } else {
        icon.addEventListener('click', (e) => { if (this.current !== item) { e.preventDefault(); this.open(item); } });
      }
      item.addEventListener('focusin', () => this.open(item));
      item.addEventListener('focusout', (e) => {
        if (this.contains(e.relatedTarget)) return;
        // focus moving into the Facebook iframe has no relatedTarget; check where it landed first
        setTimeout(() => { if (!this.contains(document.activeElement)) this.close(); }, 0);
      });
    });
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.current) { const icon = this.current.querySelector('.ik-social__icon'); this.close(); icon.focus({ preventScroll: true }); }
    });
    this.addEventListener('click', (e) => {
      const go = e.target.closest('[data-ik-goto]'); if (!go) return;
      e.preventDefault();
      const target = this.querySelector(`.ik-social__item[data-brand="${go.dataset.ikGoto}"]`);
      if (target) { this.open(target); target.querySelector('.ik-btn')?.focus({ preventScroll: true }); }
    });
    document.addEventListener('click', (e) => { if (this.current && !this.contains(e.target)) this.close(); });
  }
  #later(fn, ms) { clearTimeout(this.timer); this.timer = setTimeout(fn, ms); }
  open(item) {
    clearTimeout(this.timer);
    if (this.current && this.current !== item) this.close();
    const icon = item.querySelector('.ik-social__icon');
    const card = item.querySelector('.ik-card');
    const bar = this.getBoundingClientRect(), r = icon.getBoundingClientRect();
    card.style.setProperty('--caret', `${Math.round(bar.right - (r.left + r.width / 2))}px`);
    item.classList.add('is-open'); icon.setAttribute('aria-expanded', 'true');
    hydrate(card); this.current = item;
  }
  close() {
    if (!this.current) return;
    this.current.classList.remove('is-open');
    this.current.querySelector('.ik-social__icon').setAttribute('aria-expanded', 'false');
    this.current = null;
  }

  /* ---- drawer row: opens the sheet ---- */
  #drawer() {
    const sheet = this.querySelector('inksake-social-sheet');
    if (sheet) document.body.appendChild(sheet); // portal out of the transformed drawer
    this.sheet = sheet || document.querySelector('inksake-social-sheet');
    this.querySelectorAll('[data-ik-open]').forEach((btn) => btn.addEventListener('click', () => this.sheet?.open(btn.dataset.ikOpen, btn)));
  }
}

class InksakeSocialSheet extends HTMLElement {
  connectedCallback() {
    if (this.ready) return; this.ready = true;
    this.root = this.querySelector('.ik-sheet');
    this.panel = this.querySelector('.ik-sheet__panel');
    this.body = this.querySelector('.ik-sheet__body');
    this.tabs = [...this.querySelectorAll('.ik-tab')];
    this.tabs.forEach((t) => t.addEventListener('click', () => this.select(t.dataset.brand, true)));
    this.querySelector('[role="tablist"]').addEventListener('keydown', (e) => {
      const i = this.tabs.indexOf(document.activeElement); if (i < 0) return;
      const n = { ArrowRight: 1, ArrowLeft: -1, Home: -i, End: this.tabs.length - 1 - i }[e.key]; if (n === undefined) return;
      e.preventDefault(); const t = this.tabs[(i + n + this.tabs.length) % this.tabs.length]; this.select(t.dataset.brand, true); t.focus();
    });
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-ik-close]')) return this.close();
      const go = e.target.closest('[data-ik-goto]'); if (go) { e.preventDefault(); this.select(go.dataset.ikGoto, true); }
    });
    this.addEventListener('keydown', (e) => this.#keys(e));
    this.addEventListener('keyup', (e) => { if (e.key === 'Escape') e.stopPropagation(); });
    this.#swipe();
    // The theme's drawer traps focus with document focusin listeners; the sheet sits outside the drawer (portaled
    // to <body>), so keep those listeners from pulling focus back while the sheet is open.
    const guard = (e) => { if (this.isOpen && this.contains(e.target)) e.stopImmediatePropagation(); };
    window.addEventListener('focusin', guard, true); window.addEventListener('focusout', guard, true);
    addEventListener('popstate', () => { if (this.isOpen && this.pushed) { this.pushed = false; this.close(); } });
  }
  open(id, opener) {
    this.opener = opener; this.isOpen = true;
    this.root.hidden = false;
    document.documentElement.classList.add('ik-sheet-lock');
    this.select(id, false);
    requestAnimationFrame(() => requestAnimationFrame(() => this.root.classList.add('is-open')));
    try { history.pushState({ ikSheet: 1 }, ''); this.pushed = true; } catch (_) { this.pushed = false; }
    setTimeout(() => this.querySelector('.ik-sheet__close').focus({ preventScroll: true }), reduce() ? 0 : 60);
  }
  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    if (this.pushed) { this.pushed = false; history.back(); }
    this.root.classList.remove('is-open');
    this.panel.style.transform = '';
    const done = () => { this.root.hidden = true; document.documentElement.classList.remove('ik-sheet-lock'); };
    reduce() ? done() : setTimeout(done, 380);
    this.opener?.focus({ preventScroll: true });
  }
  select(id, user) {
    this.tabs.forEach((t) => { const on = t.dataset.brand === id; t.setAttribute('aria-selected', on); t.tabIndex = on ? 0 : -1; });
    this.querySelectorAll('.ik-pane').forEach((p) => { p.hidden = p.dataset.brand !== id; if (!p.hidden) hydrate(p); });
    this.querySelectorAll('[data-ik-foot]').forEach((a) => { a.hidden = a.dataset.ikFoot !== id; });
    if (user) this.body.scrollTop = 0;
  }
  #keys(e) {
    if (e.key === 'Escape') {
      e.preventDefault(); e.stopPropagation();
      // focus returns to the opener inside the drawer before keyup fires; swallow that keyup so the theme's
      // drawer (which closes on Escape keyup) stays open underneath.
      const eat = (k) => { if (k.key === 'Escape') { k.stopImmediatePropagation(); removeEventListener('keyup', eat, true); } };
      addEventListener('keyup', eat, true); setTimeout(() => removeEventListener('keyup', eat, true), 1000);
      return this.close();
    }
    if (e.key !== 'Tab') return;
    const f = [...this.panel.querySelectorAll('a[href], button:not([disabled])')].filter((el) => el.offsetParent !== null && el.tabIndex >= 0);
    if (!f.length) return;
    const first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }
  #swipe() {
    let y0 = 0, dy = 0, t0 = 0, drag = false;
    this.panel.addEventListener('touchstart', (e) => {
      if (e.target.closest('.ik-sheet__body') && this.body.scrollTop > 0) return;
      y0 = e.touches[0].clientY; dy = 0; t0 = Date.now(); drag = true;
    }, { passive: true });
    this.panel.addEventListener('touchmove', (e) => {
      if (!drag) return;
      dy = e.touches[0].clientY - y0;
      if (dy <= 0) { this.root.classList.remove('is-dragging'); this.panel.style.transform = ''; return; }
      if (e.cancelable) e.preventDefault();
      this.root.classList.add('is-dragging');
      this.panel.style.transform = `translateY(${dy}px)`;
    }, { passive: false });
    this.panel.addEventListener('touchend', () => {
      if (!drag) return; drag = false;
      this.root.classList.remove('is-dragging');
      const v = dy / Math.max(1, Date.now() - t0);
      if (dy > 120 || (dy > 40 && v > 0.6)) this.close(); else this.panel.style.transform = '';
    });
  }
}

if (!customElements.get('inksake-social-sheet')) customElements.define('inksake-social-sheet', InksakeSocialSheet);
if (!customElements.get('inksake-social')) customElements.define('inksake-social', InksakeSocial);
